import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AlertTriangle, ShieldOff } from 'lucide-react';
import Button from '../Button';
import ShipmentsGate from './ShipmentsGate';
import { MessageCard, PageLoading } from './ShipmentsStates';
import { GuardContext } from './guardContext';
import { CODES, api, onVerificationRequired } from '../../utils/shipmentsApi';
import './shipments.css';

const EXPIRED_NOTICE = 'Ověření vypršelo, zadejte nový kód.';

// Gate of the whole Zásilky module (all /zasilky pages render inside it through <Outlet />).
// phase: loading -> gate | ready | denied | error. This is the phase-0 state machine, moved here unchanged in behaviour:
// status check, e-mail code gate, protected /ping, and a flip back to the gate on any VERIFICATION_REQUIRED answer.
const ShipmentsGuard = () => {
  const [phase, setPhase] = useState('loading');
  const [status, setStatus] = useState(null);
  const [notice, setNotice] = useState('');
  const [loadError, setLoadError] = useState('');
  const [ping, setPing] = useState('idle'); // idle | ok | failed
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const showGate = useCallback((message = '') => {
    setNotice(message);
    setPing('idle');
    setLockError('');
    setStatus((s) => (s ? { ...s, verified: false, expiresAt: null, idleExpiresAt: null } : s));
    setPhase('gate');
  }, []);

  // 1) Ask the server whether this session is already verified
  useEffect(() => {
    let cancelled = false;
    api('/verify/status')
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        setPhase(data.verified ? 'ready' : 'gate');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.code === CODES.NO_ACCESS) { setPhase('denied'); return; }
        setLoadError(err.message);
        setPhase('error');
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // 2) Prove the protected endpoint works, then refresh the status (the call also moves the idle expiry)
  useEffect(() => {
    if (phase !== 'ready') return undefined;
    let cancelled = false;
    api('/ping')
      .then(() => {
        if (cancelled) return null;
        setPing('ok');
        return api('/verify/status').catch(() => null);
      })
      .then((data) => {
        if (cancelled || !data) return;
        if (data.verified) setStatus(data); else showGate(EXPIRED_NOTICE);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.code === CODES.NO_ACCESS) setPhase('denied');
        else if (err.code !== CODES.VERIFICATION_REQUIRED) setPing('failed'); // that one is handled below
      });
    return () => { cancelled = true; };
  }, [phase, showGate]);

  // 3) Any protected call that reports a lost verification flips back to the gate
  useEffect(() => {
    if (phase !== 'ready') return undefined;
    return onVerificationRequired(() => showGate(EXPIRED_NOTICE));
  }, [phase, showGate]);

  const handleVerified = (data) => {
    setNotice('');
    setStatus((s) => ({ ...s, ...data, verified: true }));
    setPhase('ready');
  };

  const handleRetry = () => {
    setPhase('loading');
    setReloadKey((k) => k + 1);
  };

  const lock = useCallback(async () => {
    setLocking(true);
    setLockError('');
    try {
      await api('/verify/lock', { method: 'POST' });
      showGate();
    } catch (err) {
      if (err.code === CODES.NO_ACCESS) setPhase('denied');
      else if (err.code !== CODES.VERIFICATION_REQUIRED) setLockError(err.message);
    } finally {
      setLocking(false);
    }
  }, [showGate]);

  const ctx = useMemo(() => ({ status, ping, lock, locking, lockError }), [status, ping, lock, locking, lockError]);

  if (phase === 'loading') return <PageLoading />;
  if (phase === 'denied') {
    return (
      <MessageCard icon={ShieldOff} title="Nemáte oprávnění">
        K modulu Zásilky nemáte přístup. Pokud ho potřebujete, požádejte správce.
      </MessageCard>
    );
  }
  if (phase === 'error') {
    return (
      <MessageCard icon={AlertTriangle} tone="#E65100" toneBg="#FFF3E0" title="Modul se nepodařilo načíst"
        action={<Button variant="secondary" onClick={handleRetry}>Zkusit znovu</Button>}>
        {loadError}
      </MessageCard>
    );
  }
  if (phase === 'gate') return <ShipmentsGate status={status} notice={notice} onVerified={handleVerified} />;

  return <GuardContext.Provider value={ctx}><Outlet /></GuardContext.Provider>;
};

export default ShipmentsGuard;
